import type { ContextBlock, PipelineStage } from '../types';
import { draftText } from '../draft-text';
import { elideLargeLiterals } from '../../parsing/elide-literals';

export const b2LargeLiteralElision: PipelineStage = {
  id: 'B2',
  group: 'B',
  label: 'Large literal elision',
  description: 'Replace long literals (arrays, base64 blobs, generated code) with a short placeholder comment.',
  impact: 'Medium',
  defaultEnabled: true,
  implemented: true,
  params: {
    thresholdChars: { type: 'number', default: 200, min: 20, max: 5000, label: 'Elide literals longer than (chars)' },
  },

  async run(draft, params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));
    const threshold = Number(params.thresholdChars) || 200;
    const langByPath = new Map(draft.openFiles.map((f) => [f.path, f.languageId]));

    const newBlocks: ContextBlock[] = [];
    let changedCount = 0;
    let warning: string | undefined;

    for (const block of draft.contextBlocks) {
      if (!block.id.startsWith('file:')) {
        newBlocks.push(block);
        continue;
      }
      const path = block.id.slice('file:'.length);
      const languageId = langByPath.get(path);
      if (!languageId) {
        newBlocks.push(block);
        continue;
      }
      try {
        const elided = await elideLargeLiterals(block.content, languageId, threshold);
        if (elided !== block.content) changedCount++;
        // Eliding shifts every offset after the elision, so B1's spans no
        // longer describe this content. Drop them rather than hand B4 stale
        // ranges — untagged text is treated as code and left alone.
        newBlocks.push(elided === block.content ? block : { ...block, content: elided, spans: undefined });
      } catch (err) {
        newBlocks.push(block);
        warning = err instanceof Error ? err.message : String(err);
      }
    }

    const newDraft = { ...draft, contextBlocks: newBlocks };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'B2',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [changedCount > 0 ? `Elided large literals in ${changedCount} file block(s).` : 'No literals over the threshold found.'],
      },
      warning,
    };
  },
};
